import { useEffect, useRef } from 'react';

import { AdornedInput, Box, FieldSet, FormField, FormHelp, Label, Legend } from '@zillow/constellation-10';

import numberUtils from '../../../utils/numberUtils';

import type { MutableRefObject } from 'react';
import type { FeeCalculationDictionaryItemDisplayDto } from '@zg-rentals/ts-rental-manager-api-client';

const FEE_PERCENTAGE_ADORNMENTS = {
  PERCENTAGE_OF_RENT: '% of base rent',
  PERCENTAGE_OF_SECURITY_DEPOSIT: '% of security deposit',
  PER_ITEM_PERCENTAGE_OF_RENT: '% of base rent',
} as const;

const getInitialDollarAmount = (value: number, ref: MutableRefObject<boolean>) =>
  ref.current ? Number(value).toFixed(2) : value;

type FeePercentageAdornmentKey = keyof typeof FEE_PERCENTAGE_ADORNMENTS;

type Props = {
  setFeeAmountMin: (value: number) => void;
  setFeeAmountMax: (value: number) => void;
  valueMin: number;
  valueMax: number;
  feeFormat: FeeCalculationDictionaryItemDisplayDto;
  feeFormatName: string;
  onBlur: (fieldName: string, value: number) => void;
  feeErrors: Record<string, string>;
};
// TODO: update the adornments to be more specific for each type (see figma field anatomy)
const FeeAmountField = ({
  setFeeAmountMin,
  setFeeAmountMax,
  valueMin,
  valueMax,
  feeFormat,
  feeFormatName,
  onBlur,
  feeErrors,
}: Props) => {
  let rangeFormHelpError = '';

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    }
  }, []);

  const handleInputFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.valueAsNumber === 0) {
      e.target.select();
    }
  };

  switch (true) {
    case feeErrors?.feeAmountMin !== undefined &&
      feeErrors?.feeAmountMin !== '' &&
      feeErrors?.feeAmountMax !== undefined &&
      feeErrors?.feeAmountMax !== '':
      rangeFormHelpError = `Minimum: ${feeErrors?.feeAmountMin} Maximum: ${feeErrors?.feeAmountMax}`;
      break;
    case feeErrors?.feeAmountMin !== undefined && feeErrors?.feeAmountMin !== '':
      rangeFormHelpError = feeErrors?.feeAmountMin;
      break;
    case feeErrors?.feeAmountMax !== undefined && feeErrors?.feeAmountMax !== '':
      rangeFormHelpError = feeErrors?.feeAmountMax;
      break;
  }

  switch (feeFormat.feeCalculationFieldType) {
    case 'NONE':
      return null;
    case 'PERCENTAGE':
      return (
        <FormField
          error={Boolean(feeErrors?.feeAmountPercent)}
          description={<FormHelp>{feeErrors?.feeAmountPercent || feeFormat.feeCalculationFooterMessage}</FormHelp>}
          required={true}
          label={<Label>Enter a percentage</Label>}
        >
          <AdornedInput
            input={
              <AdornedInput.Input
                type="number"
                value={valueMin}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFeeAmountMin(Number(Number(e.target.valueAsNumber).toFixed(2)))
                }
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  onBlur('feeAmountPercent', e.target.valueAsNumber);
                }}
              />
            }
            endAdornment={
              <AdornedInput.Adornment>
                {FEE_PERCENTAGE_ADORNMENTS[feeFormatName as FeePercentageAdornmentKey] || '%'}
              </AdornedInput.Adornment>
            }
          />
        </FormField>
      );
    case 'MULTIPLE':
      return (
        <FormField
          error={Boolean(feeErrors?.feeAmountMultiple)}
          description={<FormHelp>{feeErrors?.feeAmountMultiple || feeFormat.feeCalculationFooterMessage}</FormHelp>}
          required={true}
          label={<Label>Enter a multiple</Label>}
        >
          <AdornedInput
            input={
              <AdornedInput.Input
                type="number"
                value={valueMin}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFeeAmountMin(Number(Number(e.target.valueAsNumber).toFixed(2)))
                }
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  onBlur('feeAmountMultiple', e.target.valueAsNumber);
                }}
              />
            }
            endAdornment={<AdornedInput.Adornment>times base rent</AdornedInput.Adornment>}
          />
        </FormField>
      );
    case 'RANGE':
      return (
        <FieldSet
          legend={<Legend>Fee amount range</Legend>}
          error={rangeFormHelpError !== ''}
          description={<FormHelp>{rangeFormHelpError || feeFormat.feeCalculationFooterMessage}</FormHelp>}
        >
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: { base: 'repeat(12, 1fr)', lgDown: 'repeat(6, 1fr)' },
              columnGap: '400',
            }}
          >
            <Box css={{ display: 'grid', gridColumnEnd: 'span 6' }}>
              <FormField required={true} label={<Label>Minimum</Label>} error={rangeFormHelpError !== ''}>
                <AdornedInput
                  input={
                    <AdornedInput.Input
                      type="number"
                      value={getInitialDollarAmount(valueMin, isInitialRender)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.valueAsNumber;

                        if (numberUtils.hasMoreThanTwoDecimalPlaces(value)) {
                          // do not allow any more typing
                          return;
                        }

                        const valueAsCurrency = numberUtils.asCurrency(Number(value), '0.00');

                        // converting to a number drops the hundredths place if it is zero
                        setFeeAmountMin(Number(valueAsCurrency));

                        // select input field after the updated value is set
                        requestAnimationFrame(() => {
                          handleInputFocus(e);
                        });

                        onBlur('feeAmountMin', Number(valueAsCurrency));
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        const value = e.target.valueAsNumber;
                        const valueAsCurrency = numberUtils.asCurrency(Number(value), '0.00');

                        onBlur('feeAmountMin', Number(valueAsCurrency));
                      }}
                      onFocus={handleInputFocus}
                    />
                  }
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                />
              </FormField>
            </Box>
            <Box css={{ display: 'grid', gridColumnEnd: 'span 6' }}>
              <FormField required={true} label={<Label>Maximum</Label>} error={rangeFormHelpError !== ''}>
                <AdornedInput
                  input={
                    <AdornedInput.Input
                      type="number"
                      value={getInitialDollarAmount(valueMax, isInitialRender)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.valueAsNumber;

                        if (numberUtils.hasMoreThanTwoDecimalPlaces(value)) {
                          // do not allow any more typing
                          return;
                        }

                        const valueAsCurrency = numberUtils.asCurrency(Number(value), '0.00');

                        // converting to a number drops the hundredths place if it is zero
                        setFeeAmountMax(Number(valueAsCurrency));

                        // select input field after the updated value is set
                        requestAnimationFrame(() => {
                          handleInputFocus(e);
                        });

                        onBlur('feeAmountMax', Number(valueAsCurrency));
                      }}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        const value = e.target.valueAsNumber;
                        const valueAsCurrency = numberUtils.asCurrency(Number(value), '0.00');

                        onBlur('feeAmountMax', Number(valueAsCurrency));
                      }}
                      onFocus={handleInputFocus}
                    />
                  }
                  startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                />
              </FormField>
            </Box>
          </Box>
        </FieldSet>
      );
    case 'FIXED':
    default:
      return (
        <FormField
          error={feeFormatName === 'FLAT_FEE' ? Boolean(feeErrors?.feeAmountMinFlat) : Boolean(feeErrors?.feeAmountMin)}
          description={
            <FormHelp>
              {feeFormatName === 'FLAT_FEE'
                ? feeErrors?.feeAmountMinFlat
                : feeErrors?.feeAmountMin || feeFormat.feeCalculationFooterMessage}
            </FormHelp>
          }
          required={true}
          label={<Label>Fee amount</Label>}
        >
          <AdornedInput
            input={
              <AdornedInput.Input
                type="number"
                value={getInitialDollarAmount(valueMin, isInitialRender)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.valueAsNumber;

                  if (numberUtils.hasMoreThanTwoDecimalPlaces(value)) {
                    // do not allow any more typing
                    return;
                  }

                  const valueAsCurrency = numberUtils.asCurrency(Number(value), '0.00');

                  // converting to a number drops the hundredths place if it is zero
                  setFeeAmountMin(Number(valueAsCurrency));

                  // select input field after the updated value is set
                  requestAnimationFrame(() => {
                    handleInputFocus(e);
                  });

                  onBlur(feeFormatName === 'FLAT_FEE' ? 'feeAmountMinFlat' : 'feeAmountMin', Number(valueAsCurrency));
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  const value = e.target.valueAsNumber;
                  const valueAsCurrency = numberUtils.asCurrency(Number(value), '0.00');

                  onBlur(feeFormatName === 'FLAT_FEE' ? 'feeAmountMinFlat' : 'feeAmountMin', Number(valueAsCurrency));
                }}
                onFocus={handleInputFocus}
              />
            }
            startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
            data-testid="FeeAmountField-Input"
          />
        </FormField>
      );
  }
};

export default FeeAmountField;
